import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Brain, ArrowLeft, Mail, Phone, MapPin, Clock, Send, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CONTACT_INFO = [
  {
    icon: Mail,
    label: "Email Us",
    value: "support@shansoai.com",
    href: "mailto:support@shansoai.com",
    desc: "We respond within 24 hours on business days"
  },
  {
    icon: Clock,
    label: "Support Hours",
    value: "Mon – Sat: 9:00 AM – 6:00 PM IST",
    href: null,
    desc: "We're here to help during business hours"
  },
  {
    icon: MapPin,
    label: "Location",
    value: "Tamil Nadu, India",
    href: null,
    desc: "Online & in-person AI trainings"
  },
];

export default function ContactUs() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", company: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/support/contact`, form);
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send. Please try again or email support@shansoai.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="marketing-bg min-h-screen">
      <nav className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-dm-sans">
            <ArrowLeft size={16} /> Back to Home
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Brain size={16} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF] text-sm">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Get in Touch</span>
          <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white mt-3 mb-4">Contact Us</h1>
          <p className="text-[#A1A1AA] text-base font-dm-sans max-w-xl mb-12">
            Have a question about a training, payment, or certificate? We're here to help. Reach out and we'll get back to you as soon as possible.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3"
          >
            {sent ? (
              <div className="bg-zinc-900/50 border border-zinc-800 p-10 text-center">
                <CheckCircle size={56} className="text-[#00F0FF] mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="font-outfit font-bold text-white text-2xl mb-2">Message Sent!</h3>
                <p className="text-[#A1A1AA] font-dm-sans text-sm mb-6">Thanks for reaching out — we've received your message and will reply within 24 hours on business days. Check your inbox for an acknowledgement.</p>
                <button
                  onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
                  className="text-[#00F0FF] text-sm font-dm-sans hover:underline"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-zinc-900/50 border border-zinc-800 p-8 space-y-5">
                <h2 className="font-outfit font-bold text-white text-xl mb-1">Send Us a Message</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wide font-dm-sans">Full Name *</label>
                    <input
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Your full name"
                      className="w-full bg-zinc-800/60 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 py-3 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wide font-dm-sans">Email Address *</label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      placeholder="your@email.com"
                      className="w-full bg-zinc-800/60 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 py-3 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wide font-dm-sans">Phone Number</label>
                    <input
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                      placeholder="Your phone number"
                      className="w-full bg-zinc-800/60 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 py-3 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wide font-dm-sans">Subject</label>
                    <input
                      data-testid="contact-subject-input"
                      value={form.subject}
                      onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                      placeholder="Training inquiry, certificate, etc."
                      className="w-full bg-zinc-800/60 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 py-3 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5 uppercase tracking-wide font-dm-sans">Message *</label>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                    placeholder="Tell us how we can help you..."
                    rows={5}
                    className="w-full bg-zinc-800/60 border border-zinc-700 focus:border-[#00F0FF] text-white px-4 py-3 text-sm font-dm-sans placeholder:text-zinc-600 outline-none transition-colors resize-none"
                    required
                  />
                </div>
                {error && <p className="text-red-400 text-sm font-dm-sans">{error}</p>}
                {/* Honeypot — must remain empty. Hidden from real users. */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.company}
                  onChange={e => setForm(p => ({ ...p, company: e.target.value }))}
                  style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                  aria-hidden="true"
                />
                <button
                  data-testid="contact-submit-btn"
                  type="submit"
                  disabled={sending}
                  className="w-full bg-[#00F0FF] text-black font-outfit font-black py-4 text-base hover:bg-white transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Sending...</>
                  ) : (
                    <><Send size={18} /> Send Message</>
                  )}
                </button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-4"
          >
            {CONTACT_INFO.map((info, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 p-5 hover:border-[#00F0FF]/30 transition-colors group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#00F0FF]/20 transition-colors">
                    <info.icon size={18} className="text-[#00F0FF]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-outfit font-bold text-white text-sm mb-1">{info.label}</h3>
                    {info.href ? (
                      <a href={info.href} className="text-[#00F0FF] text-sm font-dm-sans hover:underline break-all">{info.value}</a>
                    ) : (
                      <p className="text-[#00F0FF] text-sm font-dm-sans">{info.value}</p>
                    )}
                    <p className="text-[#A1A1AA] text-xs font-dm-sans mt-1">{info.desc}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* FAQ quick links */}
            <div className="bg-zinc-900/50 border border-zinc-800 p-5">
              <h3 className="font-outfit font-bold text-white text-sm mb-3">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/refund-policy" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">
                  → Refund Policy
                </Link>
                <Link to="/terms" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">
                  → Terms & Conditions
                </Link>
                <Link to="/privacy-policy" className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#00F0FF] text-sm font-dm-sans transition-colors">
                  → Privacy Policy
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <footer className="border-t border-[#27272A] py-6 px-6 mt-10">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <p className="text-[#A1A1AA] text-xs font-dm-sans">© {new Date().getFullYear()} ShansoAI Hub. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Privacy Policy</Link>
            <Link to="/terms" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Terms</Link>
            <Link to="/refund-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Refund Policy</Link>
            <Link to="/contact" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
